import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Input, Space, Tag, Avatar, Button, Badge, Select } from 'antd';
import { SearchOutlined, UserOutlined, EyeOutlined, FilterOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { UserFilters, UserFilterValues } from '../../components/filters/UserFilters';

interface User {
  id: number;
  fullName: string | null;
  phone: string;
  currentRole: 'CLIENT' | 'DRIVER' | 'ADMIN';
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED' | 'RESTING';
  photoUrl: string | null;
  rating: number;
  totalTrips: number;
  isDriverVerified: boolean;
  createdAt: string;
}

export const UserList = () => {
  const { show } = useNavigation();
  const [searchText, setSearchText] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<UserFilterValues>({});

  const activeFilterCount = Object.values(activeFilters).filter(
    (v) => v !== undefined && v !== '' && v !== null
  ).length;

  const { tableProps, setFilters } = useTable<User>({
    resource: 'admin/users',
    syncWithLocation: true,
    filters: {
      initial: [],
    },
  });

  const handleApplyFilters = (filters: UserFilterValues) => {
    setActiveFilters(filters);

    const refineFilters: any[] = [];

    if (filters.search) {
      refineFilters.push({ field: 'search', operator: 'contains', value: filters.search });
    }
    if (filters.phone) {
      refineFilters.push({ field: 'phone', operator: 'contains', value: filters.phone });
    }
    if (filters.role) {
      refineFilters.push({ field: 'role', operator: 'eq', value: filters.role });
    }
    if (filters.status) {
      refineFilters.push({ field: 'status', operator: 'eq', value: filters.status });
    }
    if (filters.isDriverVerified !== undefined) {
      refineFilters.push({ field: 'isDriverVerified', operator: 'eq', value: filters.isDriverVerified });
    }
    if (filters.minTrips !== undefined) {
      refineFilters.push({ field: 'minTrips', operator: 'gte', value: filters.minTrips });
    }
    if (filters.maxTrips !== undefined) {
      refineFilters.push({ field: 'maxTrips', operator: 'lte', value: filters.maxTrips });
    }
    if (filters.minRating !== undefined) {
      refineFilters.push({ field: 'minRating', operator: 'gte', value: filters.minRating });
    }
    if (filters.maxRating !== undefined) {
      refineFilters.push({ field: 'maxRating', operator: 'lte', value: filters.maxRating });
    }
    if (filters.registeredFrom) {
      refineFilters.push({ field: 'registeredFrom', operator: 'gte', value: filters.registeredFrom });
    }
    if (filters.registeredTo) {
      refineFilters.push({ field: 'registeredTo', operator: 'lte', value: filters.registeredTo });
    }

    setFilters(refineFilters);
  };

  const handleResetFilters = () => {
    setActiveFilters({});
    setFilters([]);
  };

  const statusColors: Record<string, string> = {
    ONLINE: 'green',
    OFFLINE: 'default',
    BLOCKED: 'red',
    RESTING: 'orange',
  };

  const statusLabels: Record<string, string> = {
    ONLINE: 'Faol',
    OFFLINE: 'Nofaol',
    BLOCKED: 'Bloklangan',
    RESTING: 'Dam olmoqda',
  };

  const roleLabels: Record<string, string> = {
    CLIENT: "Yo'lovchi",
    DRIVER: 'Haydovchi',
    ADMIN: 'Admin',
  };

  const columns = [
    {
      title: 'Foydalanuvchi',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (_: string, record: User) => (
        <Space>
          <Avatar src={record.photoUrl} icon={<UserOutlined />} />
          <div>
            <div>{record.fullName || "Ismi yo'q"}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Rol',
      dataIndex: 'currentRole',
      key: 'currentRole',
      render: (role: string) => (
        <Tag color={role === 'DRIVER' ? 'blue' : role === 'ADMIN' ? 'purple' : 'default'}>
          {roleLabels[role] || role}
        </Tag>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: 'Reyting',
      dataIndex: 'rating',
      key: 'rating',
      render: (rating: number | string) => rating != null ? Number(rating).toFixed(1) : '-',
    },
    {
      title: 'Sayohatlar',
      dataIndex: 'totalTrips',
      key: 'totalTrips',
    },
    {
      title: "Ro'yxatdan o'tgan",
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: unknown, record: User) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => show('admin/users', record.id)}
        />
      ),
    },
  ];

  return (
    <>
      <List
        title="Barcha foydalanuvchilar"
        headerButtons={
          <Space>
            <Input
              placeholder="Qidirish..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
              onPressEnter={() => handleApplyFilters({ ...activeFilters, search: searchText })}
            />
            <Select
              placeholder="Rol"
              allowClear
              style={{ width: 130 }}
              value={activeFilters.role}
              onChange={(value) => handleApplyFilters({ ...activeFilters, role: value })}
              options={[
                { value: 'CLIENT', label: "Yo'lovchi" },
                { value: 'DRIVER', label: 'Haydovchi' },
              ]}
            />
            <Select
              placeholder="Holat"
              allowClear
              style={{ width: 130 }}
              value={activeFilters.status}
              onChange={(value) => handleApplyFilters({ ...activeFilters, status: value })}
              options={[
                { value: 'ONLINE', label: 'Faol' },
                { value: 'OFFLINE', label: 'Nofaol' },
                { value: 'BLOCKED', label: 'Bloklangan' },
              ]}
            />
            <Badge count={activeFilterCount} size="small">
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterOpen(true)}
              >
                Filtr
              </Button>
            </Badge>
          </Space>
        }
      >
        <Table {...tableProps} columns={columns} rowKey="id" />
      </List>

      <UserFilters
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        filterType="all"
        initialValues={activeFilters}
      />
    </>
  );
};
